import { useRef } from "react";

import { baseUrl } from "@/api/config";
import { Timezone } from "@/constants/dateTime";
import {
  getDeepestAdminFilterFirst,
  getStreamUrlDatasetStream,
  mapDatasetFiltersToPayload
} from "@/helpers/datasetAttributeHelper";
import {
  Dataset,
  DatasetChunkStream,
  DatasetData,
  DatasetDataRowV2,
  DatasetDetailResponse,
  DatasetFilter,
  DatasetFilterPayload,
  DatasetMetadata,
  DatasetMetadataStream,
  DatasetStreamDone,
  H3Configuration
} from "@/types/dataset";
import { buildGridConfig } from "@/utils/dataset";

import useEventSource from "./useEventSource";

import { useDataset } from "./map/useDataset";

export interface DatasetStreamComplete {
  datasetMetadataStream: DatasetMetadataStream;
  datasetMetadata: DatasetMetadata;
  rawDataset: DatasetData;
  dataset: Dataset;
}

interface IUseDatasetStreamProps {
  datasetId?: string;
  enabled: boolean;
  filters?: DatasetFilter[];
  timezone?: Timezone;
  h3Configuration?: H3Configuration | null;
  group_by?: string;
  show_id?: boolean;
  onOpen?: () => void;
  onDatasetMetadata?: (data: DatasetMetadata) => void;
  onStreamComplete?: (data: DatasetStreamComplete) => void | Promise<void>;
  onError?: (data: unknown) => void;
  onRetriesExhausted?: () => void;
  analysisId?: string;
  showAnalysisResultPopup?: boolean;
  datasetDetail?: DatasetDetailResponse | null;
}

type Error = { code: number; message: string };

export type DatasetStream = {
  dataset_detail: DatasetMetadata;
  "dataset_data:meta": DatasetMetadataStream;
  "dataset_data:chunk": DatasetChunkStream;
  "dataset_data:done": DatasetStreamDone;
  "stream:done": { dataset_id: string };
  "stream:error": Error;
  "stream:reset": Error;
  error: Error;
};

export function useDatasetStream({
  datasetId,
  filters,
  timezone,
  h3Configuration,
  enabled,
  show_id,
  onStreamComplete,
  onError,
  onOpen,
  onRetriesExhausted,
  analysisId,
  showAnalysisResultPopup,
  datasetDetail,
  group_by
}: IUseDatasetStreamProps) {
  const { generateDatasetData } = useDataset();

  const chunkBufferRef = useRef<DatasetDataRowV2[]>([]);
  const datasetMetadataRef = useRef<DatasetMetadata>();
  const datasetMetadataStreamRef = useRef<DatasetMetadataStream>();
  const datasetDataRef = useRef<{
    rawDataset: DatasetData;
    dataset: Dataset;
  }>();

  const handleDatasetDone = (data: DatasetStreamDone) => {
    if (
      showAnalysisResultPopup &&
      datasetDetail &&
      !datasetMetadataRef.current
    ) {
      datasetMetadataRef.current = datasetDetail as DatasetMetadata;
    }

    const datasetChunk = {
      dataset_id: data.dataset_id,
      rows: chunkBufferRef.current,
      seq: data.final_seq
    };

    if (datasetMetadataStreamRef.current && datasetMetadataRef.current) {
      const { datasetData: rawDataset, newDataset: dataset } =
        generateDatasetData(
          datasetChunk,
          datasetMetadataStreamRef.current,
          true,
          h3Configuration
        );

      datasetDataRef.current = {
        rawDataset,
        dataset
      };
    }

    chunkBufferRef.current = [];
  };

  const handleStreamDone = () => {
    if (
      datasetMetadataRef.current &&
      datasetDataRef.current &&
      datasetMetadataStreamRef.current
    ) {
      // The input-rows limit is a spatial-analysis concern; consumers enforce it
      // in `onStreamComplete` (where the streamed row count is known).
      onStreamComplete?.({
        datasetMetadataStream: datasetMetadataStreamRef?.current,
        datasetMetadata: datasetMetadataRef?.current,
        rawDataset: datasetDataRef?.current?.rawDataset,
        dataset: datasetDataRef?.current?.dataset
      });
    }
  };

  const filterTransformed = getDeepestAdminFilterFirst(filters ?? []);
  const filterMap: DatasetFilterPayload[] = mapDatasetFiltersToPayload(
    filterTransformed,
    timezone
  );

  useEventSource<DatasetStream>({
    url: getStreamUrlDatasetStream({
      baseUrl,
      datasetId: datasetId,
      show_id: show_id,
      analysisId: analysisId
    }),
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      filters: filterMap,
      show_id: show_id,
      ...buildGridConfig(h3Configuration),
      ...(group_by === "none" ? {} : { group_by })
    }),
    eventKey: ["dataset-stream", datasetId, enabled],
    parse: (data) => JSON.parse(data),
    onOpen,
    eventHandlers: {
      dataset_detail: (data) => {
        datasetMetadataRef.current = data;
      },
      "dataset_data:meta": (data) => {
        datasetMetadataStreamRef.current = data;
      },
      "dataset_data:chunk": (data) => {
        // Avoid spread (`push(...rows)`): passing each row as a separate
        // argument throws "RangeError: Maximum call stack size exceeded" once a
        // large chunk exceeds the engine's argument limit. useEventSource
        // swallows that throw, leaving the buffer empty so nothing renders.
        for (const row of data.rows) chunkBufferRef.current.push(row);
      },
      "dataset_data:done": handleDatasetDone,
      "stream:done": handleStreamDone,
      "stream:error": (data) => {
        onError?.(data);
      },
      "stream:reset": (data) => {
        onError?.(data);
      },
      error: (data) => {
        onError?.(data);
      }
    },
    closeOn: ["stream:done", "stream:error", "error"],
    onRetriesExhausted: () => {
      onRetriesExhausted?.();
    },
    enabled
  });

  return {
    datasetMetadata: datasetMetadataRef.current,
    rawDataset: datasetDataRef.current?.rawDataset,
    dataset: datasetDataRef.current?.dataset
  };
}
